import { useCallback, useMemo } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import { functionWithCondition } from '@atlaskit/platform-feature-flags-react/function-with-condition';

import { useServiceCore as useService } from '@atlassian/expansion-services/core';
import {
	type FetchInput,
	requestAccess as requestAccessService,
} from '@atlassian/expansion-services/services';
import { fetchAccessRequestsCapabilities } from '@atlassian/expansion-services/requestAccessCapabilities';

import { useService as useConfluenceService } from '@confluence/expansion-services';
import { fg } from '@confluence/feature-gating';
import { cfetch } from '@confluence/network';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type { EntryPointId } from '../LoomCrossSellPopupCard/BaseLoomPopupCard.tsx';

type AnalyticsContext = {
	entryPointId: EntryPointId;
	note?: string;
	source: string;
	sourceAdType: string;
};

type AccessRequestCapability = NonNullable<
	Awaited<ReturnType<typeof fetchAccessRequestsCapabilities>>['result']
>;

const fetchJson = async (input: FetchInput) => {
	const { url, ...options } = input;
	const response = await cfetch(url, options);
	return response.json();
};

const useRequestAccessServiceLegacy = ({
	entryPointId,
	note,
	source,
	sourceAdType,
}: AnalyticsContext): (() => Promise<boolean>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId, userId } = useSessionData();
	const deps = useMemo(
		() => ({
			createAnalyticsEvent: ({ type, data }: AnalyticsEventPayload) =>
				createAnalyticsEvent({ type, data: { source, ...data } }),
			fetchJSON: fetchJson,
			getCurrentCloudId: () => cloudId,
			getCurrentUserId: () => userId ?? '',
			getCurrentProduct: () => 'confluence',
		}),
		[createAnalyticsEvent, cloudId, source, userId],
	);
	const requestAccess = useService(requestAccessService, deps);

	return useCallback(async (): Promise<boolean> => {
		try {
			const { result = '' } = await requestAccess({
				product: 'loom',
				integration: entryPointId,
				note,
			});
			const success = result === 'PENDING_REQUEST_CREATED';
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'succeeded',
					actionSubject: 'fetch',
					actionSubjectId: 'requestAccess',
					source,
					attributes: {
						adType: sourceAdType,
						entrypoint: entryPointId,
						result,
						success,
					},
				},
			}).fire();

			return success;
		} catch (error: unknown) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'fetch',
					actionSubjectId: 'requestAccess',
					source,
					attributes: {
						adType: sourceAdType,
						entrypoint: entryPointId,
						error: error instanceof Error ? error.message : 'unknown',
						result: undefined,
						success: false,
					},
				},
			}).fire();

			throw error;
		}
	}, [entryPointId, note, requestAccess, source, sourceAdType, createAnalyticsEvent]);
};

const useRequestAccessServiceGated = ({
	entryPointId,
	note,
	sourceAdType,
}: AnalyticsContext): (() => Promise<boolean>) => {
	const requestAccess = useConfluenceService(requestAccessService);

	return useCallback(async (): Promise<boolean> => {
		const { error, result } = await requestAccess({
			product: 'loom',
			integration: entryPointId,
			note,
			getAdditionalAttributes: (requestAccessResult) => ({
				adType: sourceAdType,
				entrypoint: requestAccessResult?.integration,
				result: requestAccessResult?.result,
				success: requestAccessResult?.result === 'PENDING_REQUEST_CREATED',
			}),
		});

		if (error) {
			// The platform service handles error analytics; we surface false to callers
			// instead of re-throwing to match the platform service's error contract.
			return false;
		}

		return result === 'PENDING_REQUEST_CREATED';
	}, [entryPointId, note, requestAccess, sourceAdType]);
};

export const useRequestAccessService: (ctx: AnalyticsContext) => () => Promise<boolean> =
	functionWithCondition(
		() => fg('expansion_services_loom_cross_join'),
		useRequestAccessServiceGated,
		useRequestAccessServiceLegacy,
	);

const useFetchRequestAccessCapabilityLegacy = ({
	entryPointId,
	source,
	sourceAdType,
}: AnalyticsContext): (() => Promise<AccessRequestCapability | undefined>) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	return useCallback(async () => {
		const resource = `ari:cloud:loom::site/${cloudId}`;
		try {
			const params = new URLSearchParams({
				resource,
			});
			const url = `/gateway/api/invitations/v1/access-requests/capabilities?${params.toString()}`;
			const response = await fetchJson({ url });
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'succeeded',
					actionSubject: 'fetch',
					actionSubjectId: 'requestAccessCapability',
					source,
					attributes: {
						adType: sourceAdType,
						entrypoint: entryPointId,
						resource,
					},
				},
			}).fire();
			return response.results[resource] || undefined;
		} catch (error: unknown) {
			createAnalyticsEvent({
				type: 'sendOperationalEvent',
				data: {
					action: 'failed',
					actionSubject: 'fetch',
					actionSubjectId: 'requestAccessCapability',
					source,
					attributes: {
						adType: sourceAdType,
						entrypoint: entryPointId,
						error: error instanceof Error ? error.message : 'unknown',
						resource,
					},
				},
			}).fire();
		}

		return undefined;
	}, [cloudId, createAnalyticsEvent, entryPointId, source, sourceAdType]);
};

const useFetchRequestAccessCapabilityGated = ({
	entryPointId,
	sourceAdType,
}: AnalyticsContext): (() => Promise<AccessRequestCapability | undefined>) => {
	const capabilitiesService = useConfluenceService(fetchAccessRequestsCapabilities);

	return useCallback(async () => {
		const { error, result } = await capabilitiesService({
			product: 'loom',
			integration: entryPointId,
			touchpoint: 'Crossjoin Nudge',
			getAdditionalAttributes: (capabilitiesResult) => ({
				adType: sourceAdType,
				entrypoint: capabilitiesResult?.integration,
				resource: capabilitiesResult?.resource,
			}),
		});

		if (error) {
			return undefined;
		}

		return result ?? undefined;
	}, [capabilitiesService, entryPointId, sourceAdType]);
};

export const useFetchRequestAccessCapability: (
	ctx: AnalyticsContext,
) => () => Promise<AccessRequestCapability | undefined> = functionWithCondition(
	() => fg('expansion_services_loom_cross_join'),
	useFetchRequestAccessCapabilityGated,
	useFetchRequestAccessCapabilityLegacy,
);
